import { GameError, type GameEvent } from "./types";
import type { PuzzleModule } from "./puzzle";

/**
 * Fri Verden – the 3D open world. The WORLD itself (driving, walking, the scenery) lives in the browser; this module is the part the server
 * keeps honest: which missions and races exist, when a question is due, and what counts as finished.
 *
 *  • Mission  – a few stops around the island. Reaching a stop asks ONE question; a right answer moves you on.
 *  • Race     – laps on a circuit. At the end of every lap (except the last) the car stops at the quiz line and the driver answers
 *               THREE questions before the next lap may start. The race clock is paused meanwhile, so thinking never costs you the race.
 *  • Nobody is stuck: after two wrong answers in a row on the same question slot the answer is shown and the slot is opened anyway
 *    (it just does not count as a right answer). Wrong answers never take anything away.
 *
 * Coins and XP come from the answers, exactly as in every other game (see settle.ts) – driving fast earns nothing extra, so nobody can farm the world.
 */

export type WorldVehicle = "foot" | "car" | "boat" | "air";

export interface MissionDef {
  id: string;
  title: string;
  blurb: string;
  /** number of stops = number of questions */
  steps: number;
  vehicle: WorldVehicle | "any";
  /** two stops can never be closer than this in time (stops a client from asking for questions in a burst) */
  minGapMs: number;
  /** points on the score board */
  points: number;
  /** where it is listed in the menu: the towns, the sea, the air, the sports park */
  group: MissionGroup;
}
export type MissionGroup = "by" | "hav" | "luft" | "sport";
export const MISSION_GROUPS: Record<MissionGroup, string> = { by: "I byerne", hav: "Til vands", luft: "I luften", sport: "Sportsparken" };
export const MISSIONS: MissionDef[] = [
  { id: "taxi", title: "Taxa i byen", blurb: "Hent tre gæster og kør dem hen, hvor de skal hen.", steps: 3, vehicle: "car", minGapMs: 5000, points: 100, group: "by" },
  { id: "post", title: "Pakkepost", blurb: "Kør fire pakker ud til byerne rundt om på øen.", steps: 4, vehicle: "car", minGapMs: 5000, points: 130, group: "by" },
  { id: "skat", title: "Skattestien", blurb: "Find tre steder på øen til fods eller i bil – og løs gåden ved hvert af dem.", steps: 3, vehicle: "any", minGapMs: 5000, points: 100, group: "by" },
  { id: "marked", title: "Torvedagen", blurb: "Gå rundt til boderne på torvet og hjælp sælgerne med fire spørgsmål.", steps: 4, vehicle: "foot", minGapMs: 4000, points: 110, group: "by" },
  { id: "kystbud", title: "Kystbud", blurb: "Sejl en pakke ud til bøjen, videre til Fyrøen og hjem til Havnby.", steps: 3, vehicle: "boat", minGapMs: 6000, points: 140, group: "hav" },
  { id: "perler", title: "Perlejagten", blurb: "Svøm ud til tre perler i bugten ved Strandby – ingen båd, kun dig.", steps: 3, vehicle: "foot", minGapMs: 4000, points: 120, group: "hav" },
  { id: "luftpost", title: "Luftpost", blurb: "Flyv lavt hen over tre byer og kast posten ned til dem.", steps: 3, vehicle: "air", minGapMs: 6000, points: 150, group: "luft" },
  { id: "redning", title: "Redningsaktion", blurb: "Flyv ud til tre steder, hvor nogen har brug for hjælp – bjerget, havet og skoven.", steps: 3, vehicle: "air", minGapMs: 6000, points: 160, group: "luft" },
  { id: "straffe", title: "Straffesparkskonkurrencen", blurb: "Tre straffespark mod målmanden – og et spørgsmål efter hvert.", steps: 3, vehicle: "foot", minGapMs: 4000, points: 90, group: "sport" },
  { id: "basket", title: "Basketball-frikast", blurb: "Tre frikast på banen i sportsparken.", steps: 3, vehicle: "foot", minGapMs: 4000, points: 90, group: "sport" },
  { id: "golf", title: "Golfhullet", blurb: "Slå tre bolde mod hullet på golfbanen.", steps: 3, vehicle: "foot", minGapMs: 4000, points: 90, group: "sport" },
  { id: "dart", title: "Dartskiven", blurb: "Kast tre pile mod skiven – ramt midten!", steps: 3, vehicle: "foot", minGapMs: 4000, points: 90, group: "sport" },
  { id: "tennis", title: "Tennis-serven", blurb: "Tre serv på tennisbanen – ind i feltet.", steps: 3, vehicle: "foot", minGapMs: 4000, points: 90, group: "sport" },
];

export interface RaceDef {
  id: string;
  title: string;
  blurb: string;
  laps: number;
  /** length of one lap in metres (the client's circuit is built to match) */
  lapM: number;
  /** what you race in: a car on a circuit, a boat on the coast, a plane or a helicopter along a course through the air */
  vehicle: RaceVehicle;
  /** nobody is faster than this (m/s) – the top speed of the quickest vehicle of the kind */
  vmax: number;
  /** nobody drives a lap faster than this – a lap message that arrives sooner is refused */
  minLapMs: number;
  /** computer drivers on the grid */
  ai: number;
  points: number;
}
export type RaceVehicle = "car" | "boat" | "plane" | "heli";
/** 0.8 × the length of the lap at the top speed of the fastest vehicle is a safe lower bound for a lap (cars: 52 m/s, boats 40, planes 118, helicopters 60) */
const minLap = (m: number, vmax: number) => Math.round(((m * 0.8) / vmax) * 1000);
export const RACES: RaceDef[] = [
  { id: "havn", title: "Havnebanen", blurb: "Tre omgange rundt om havnen og byen.", laps: 3, lapM: 1180, vehicle: "car", vmax: 58, minLapMs: minLap(1180, 58), ai: 3, points: 150 },
  { id: "skov", title: "Skovrallyet", blurb: "Fire omgange på den snoede skovsti.", laps: 4, lapM: 1100, vehicle: "car", vmax: 58, minLapMs: minLap(1100, 58), ai: 3, points: 190 },
  { id: "kyst", title: "Kystræset", blurb: "Tre omgange i båd mellem bøjerne ude foran Havnby.", laps: 3, lapM: 1188, vehicle: "boat", vmax: 40, minLapMs: minLap(1188, 40), ai: 3, points: 170 },
  { id: "luft", title: "Luftræset", blurb: "Flyv gennem ringene hen over hele øen – tre omgange.", laps: 3, lapM: 3944, vehicle: "plane", vmax: 118, minLapMs: minLap(3944, 118), ai: 3, points: 220 },
  { id: "heli", title: "Helikopterløbet", blurb: "Tre omgange mellem tagene og tårnene i Fri By.", laps: 3, lapM: 1449, vehicle: "heli", vmax: 60, minLapMs: minLap(1449, 60), ai: 3, points: 200 },
];
export const GATE_QUESTIONS = 3;
/** after this many wrong answers on the same slot the answer is shown and the slot opens anyway */
export const HELP_AFTER = 2;
export const SCORE_PER_RIGHT = 10;

export const missionById = (id: unknown) => MISSIONS.find((m) => m.id === id);
export const raceById = (id: unknown) => RACES.find((r) => r.id === id);

interface Active {
  kind: "mission" | "race";
  id: string;
  /** mission: stops done · race: laps finished */
  step: number;
  /** race: right (or helped) answers on the current quiz line */
  slot: number;
  misses: number;
  asking: boolean;
  at: number;
  startedAt: number;
}

export interface WorldState {
  phase: "play" | "over";
  won: boolean | null;
  active: Active | null;
  missions: Record<string, number>;
  races: Record<string, { done: number; best: number | null; place: number | null }>;
  right: number;
  helped: number;
  laps: number;
  stops: number;
  placeBonus: number;
}

export interface WorldView {
  phase: "play" | "over";
  active: null | { kind: "mission" | "race"; id: string; step: number; steps: number; slot: number; need: number; asking: boolean; laps: number };
  missions: Record<string, number>;
  races: Record<string, { done: number; best: number | null; place: number | null }>;
  right: number;
  helped: number;
  laps: number;
  stops: number;
}

const tooSoon = () => new GameError("Det gik lidt for hurtigt – kør hen til næste stop først.");

function finishMission(s: WorldState, a: Active): GameEvent[] {
  const m = missionById(a.id)!;
  s.missions[a.id] = (s.missions[a.id] ?? 0) + 1;
  s.active = null;
  s.won = true;
  return [{ kind: "mission-done", data: { id: m.id, title: m.title, points: m.points } }];
}

export const verden: PuzzleModule<WorldState, WorldView> = {
  id: "verden",
  /** no ticking clock in the world: the question simply waits (it only lapses after three minutes, and then counts softly as a miss) */
  questionSec: 180,
  create() {
    return { phase: "play", won: null, active: null, missions: {}, races: {}, right: 0, helped: 0, laps: 0, stops: 0, placeBonus: 0 };
  },
  act(s, body, now = Date.now()) {
    if (s.phase === "over") throw new GameError("Turen er slut.");
    const a = s.active;
    switch (body?.type) {
      case "mission": {
        const m = missionById(body.id);
        if (!m) throw new GameError("Den mission findes ikke.");
        if (a) throw new GameError("Du er allerede i gang med noget – afslut det først.");
        s.active = { kind: "mission", id: m.id, step: 0, slot: 0, misses: 0, asking: false, at: now, startedAt: now };
        return [{ kind: "mission-start", data: { id: m.id } }];
      }
      case "stop": {
        if (!a || a.kind !== "mission") throw new GameError("Du har ingen mission i gang.");
        if (a.asking) return [];
        if (now - a.at < missionById(a.id)!.minGapMs) throw tooSoon();
        a.asking = true;
        a.slot = 0;
        a.misses = 0;
        return [{ kind: "need-question", data: { purpose: "stop" } }];
      }
      case "race": {
        const r = raceById(body.id);
        if (!r) throw new GameError("Det ræs findes ikke.");
        if (a) throw new GameError("Du er allerede i gang med noget – afslut det først.");
        s.active = { kind: "race", id: r.id, step: 0, slot: 0, misses: 0, asking: false, at: now, startedAt: now };
        return [{ kind: "race-start", data: { id: r.id, laps: r.laps } }];
      }
      case "lap": {
        if (!a || a.kind !== "race") throw new GameError("Du kører ikke ræs lige nu.");
        if (a.asking) return [];
        const r = raceById(a.id)!;
        if (now - a.at < r.minLapMs) throw new GameError("Den omgang gik for hurtigt til at være rigtig – kør hele runden.");
        a.step++;
        s.laps++;
        a.at = now;
        if (a.step >= r.laps) {
          // the chequered flag: no quiz after the last lap
          const place = Number.isInteger(body.place) && body.place >= 1 && body.place <= r.ai + 1 ? (body.place as number) : r.ai + 1;
          const ms = Number.isFinite(body.ms) && body.ms > 0 && body.ms < 3_600_000 ? Math.round(body.ms as number) : null;
          const prev = s.races[r.id] ?? { done: 0, best: null, place: null };
          s.races[r.id] = { done: prev.done + 1, best: ms != null && (prev.best == null || ms < prev.best) ? ms : prev.best, place: prev.place == null ? place : Math.min(prev.place, place) };
          if (place === 1) s.placeBonus += 50;
          s.active = null;
          s.won = true;
          return [{ kind: "race-done", data: { id: r.id, place, ms, points: r.points } }];
        }
        a.asking = true;
        a.slot = 0;
        a.misses = 0;
        return [{ kind: "lap-done", data: { lap: a.step } }, { kind: "need-question", data: { purpose: "gate" } }];
      }
      case "cancel":
        s.active = null;
        return [{ kind: "cancel" }];
      case "end":
        s.phase = "over";
        s.won = s.won === true || Object.keys(s.missions).length + Object.keys(s.races).length > 0;
        s.active = null;
        return [{ kind: "end" }];
    }
    throw new GameError("Ukendt handling.");
  },
  onAnswer(s, correct, _d, purpose, now = Date.now()) {
    const a = s.active;
    if (!a || !a.asking || (purpose !== "stop" && purpose !== "gate")) return [];
    if (correct) s.right++;
    else a.misses++;
    const helped = !correct && a.misses >= HELP_AFTER;
    if (!correct && !helped) return [{ kind: "again", data: { misses: a.misses } }, { kind: "need-question", data: { purpose } }];
    if (helped) s.helped++;
    a.misses = 0;
    const open = (): GameEvent => ({ kind: helped ? "helped" : "ok", data: {} });
    if (purpose === "stop") {
      a.asking = false;
      a.step++;
      s.stops++;
      a.at = now;
      const m = missionById(a.id)!;
      if (a.step >= m.steps) return [open(), ...finishMission(s, a)];
      return [open(), { kind: "stop-ok", data: { step: a.step, steps: m.steps } }];
    }
    a.slot++;
    if (a.slot >= GATE_QUESTIONS) {
      a.asking = false;
      a.slot = 0;
      a.at = now;
      return [open(), { kind: "gate-open", data: { lap: a.step } }];
    }
    return [open(), { kind: "need-question", data: { purpose } }];
  },
  view(s) {
    const a = s.active;
    let active: WorldView["active"] = null;
    if (a) {
      const m = a.kind === "mission" ? missionById(a.id) : undefined;
      const r = a.kind === "race" ? raceById(a.id) : undefined;
      active = { kind: a.kind, id: a.id, step: a.step, steps: m?.steps ?? r?.laps ?? 0, slot: a.slot, need: GATE_QUESTIONS, asking: a.asking, laps: r?.laps ?? 0 };
    }
    return { phase: s.phase, active, missions: s.missions, races: s.races, right: s.right, helped: s.helped, laps: s.laps, stops: s.stops };
  },
  score(s) {
    let pts = s.right * SCORE_PER_RIGHT + s.placeBonus;
    for (const [id, n] of Object.entries(s.missions)) pts += (missionById(id)?.points ?? 0) * n;
    for (const [id, r] of Object.entries(s.races)) pts += (raceById(id)?.points ?? 0) * r.done;
    return pts;
  },
  over: (s) => s.phase === "over",
  won: (s) => s.won === true,
};
